import type { FC } from 'react';
import { useCallback } from 'react';

import type { ActionProps } from '@interstate/components/Action';
import { Action } from '@interstate/components/Action';
import { Box } from '@interstate/components/Box';
import { CarSolidIcon } from '@interstate/components/Icons';
import { ContentForm } from './components/ContentForm';
import { FormContext } from './formManager/formContext';
import useForm from './formManager/useForm';
import { TitleWrapper, VehicleInfoDrawerContentWrapper } from './VehicleInfoDrawer.style';
import { SlideOutWithFixedFooter } from '../utils/SlideOutWithFixedFooter';
import { Snackbar } from '@interstate/components/Snackbar';
import { useDispatch, useSelector } from 'react-redux';
import { REMOVE_VEHICLE_CHANGE_SUCCESS_SNACKBAR } from '@makemydeal/dr-activities-change-vehicle';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import * as vehicleActionCreators from '@makemydeal/dr-dash-store/actions/vehicleActionCreators';
import { VEHICLE_ENTITY } from '@makemydeal/dr-shared-types';
import { formatOfferTypeLabel } from '@makemydeal/dr-shared-ui-utils';
import ActionWithErrorBadge from '../header/ActionWithErrorBadge';
import { Alert } from '@interstate/components/Alert';
import type { FooterActionsType } from '@interstate/components/FooterActions';
import { paymentSelectors } from '@makemydeal/dr-offer-redux';
import { dealLockHooks } from '../hooks';

export type VehicleInfoDrawerProps = {
    actionTitle?: React.ReactNode;
    actionOverrides?: Partial<ActionProps>;
    errorBadgeHasError?: boolean;
};

export const VehicleInfoDrawer: FC<VehicleInfoDrawerProps> = ({
    actionTitle = 'Vehicle',
    actionOverrides = {},
    errorBadgeHasError
}) => {
    const dispatch = useDispatch();
    const showVehicleSuccessSnackBar = useSelector(vehicleSelectors.showVehicleChangeSnackBar);
    const cashFallbackFromOfferType = useSelector(vehicleSelectors.getCashFallbackFromOfferType);
    const showDrawer = useSelector(vehicleSelectors.showEditVehicle);
    const { error } = useSelector(paymentSelectors.getPaymentState);
    const isLocked = dealLockHooks.useIsDealLocked();

    const getSnackbarMessage = () => {
        if (cashFallbackFromOfferType) {
            const dealType = formatOfferTypeLabel(cashFallbackFromOfferType);
            return `Vehicle changed. No ${dealType} payment could be calculated, so the deal was moved to Cash.`;
        }
        return 'Vehicle Successfully Changed!';
    };

    const handleSnackbarClose = () => {
        dispatch({ type: REMOVE_VEHICLE_CHANGE_SUCCESS_SNACKBAR });
    };

    const hideDrawer = useCallback(() => dispatch(vehicleActionCreators.hideEditVehicle()), []);
    const formManager = useForm(hideDrawer);

    const footer: FooterActionsType = {
        options: [
            {
                action: () => formManager.handleClick('CANCEL'),
                label: 'Cancel',
                buttonStyle: 'tertiary',
                disabled: error,
                customIdentifiers: {
                    optionButton0: {
                        dataTestId: 'btnVehicleInfoDrawerClose',
                        id: 'VehicleInfoDrawerCloseBtn'
                    }
                }
            }
        ],
        primary: {
            action: /* istanbul ignore next */ () => formManager.handleClick('APPLY'), // Indirectly tested via button click tests; callback executed by Interstate component internals
            label: 'Apply Changes',
            buttonStyle: 'primary',
            disabled: !formManager.canApply || isLocked,
            customIdentifiers: {
                primaryButton: {
                    dataTestId: 'btnVehicleInfoDrawerApply',
                    id: 'VehicleInfoDrawerApplyBtn'
                }
            }
        }
    };

    return (
        <Box width="fit-content">
            <Action
                size="sm"
                data-testid="btnVehicleInfo"
                sx={{
                    '.ids-action': {
                        gap: '4px'
                    }
                }}
                actionIcon={{
                    icon: <CarSolidIcon />,
                    start: true
                }}
                onClick={() => {
                    dispatch(vehicleActionCreators.showEditVehicle());
                    dispatch(interactionAnalyticsActionCreators.vehicleDetailsLink());
                }}
                {...actionOverrides}
            >
                <TitleWrapper>
                    <ActionWithErrorBadge entityName={VEHICLE_ENTITY} hasError={errorBadgeHasError}>
                        {actionTitle}
                    </ActionWithErrorBadge>
                </TitleWrapper>
            </Action>

            <Snackbar
                data-testid="vehicle-change-success-popup"
                show={showVehicleSuccessSnackBar}
                message={getSnackbarMessage()}
                position="bottom-center"
                onClose={() => handleSnackbarClose()}
                type={cashFallbackFromOfferType ? 'warning' : 'success'}
                autoHide={true}
            />
            <FormContext.Provider value={formManager}>
                <SlideOutWithFixedFooter
                    data-testid="vehicle-info-drawer"
                    id="vehicle-info-drawer"
                    show={showDrawer}
                    position="right"
                    panelWidth="641px"
                    // Same path as Cancel, so closing discards edits instead of leaving them for the reopen
                    onHide={() => formManager.handleClick('CANCEL')}
                    header="Vehicle Details"
                    footer={footer}
                >
                    <VehicleInfoDrawerContentWrapper>
                        {formManager.showContractingErrorAlert && (
                            <Alert type="error" sx={{ marginBottom: '1.43rem' }}>
                                Cannot generate contract. Please fix the highlighted vehicle details before continuing.
                            </Alert>
                        )}
                        {!!formManager.missingRequiredFields.length && (
                            <Alert type="error" data-testid="vehicle-info-missing-required-alert" sx={{ marginBottom: '1.43rem' }}>
                                Missing required vehicle details: {formManager.missingRequiredFields.join(', ')}. Please fix the
                                highlighted vehicle details before continuing.
                            </Alert>
                        )}
                        <ContentForm />
                    </VehicleInfoDrawerContentWrapper>
                </SlideOutWithFixedFooter>
            </FormContext.Provider>
        </Box>
    );
};

export default VehicleInfoDrawer;
